/**
 * Study/site routing scopes (friction audit Phase 1, §8 items 8–9).
 *
 * StudyScope anchors EregContext to the study in the URL for the canonical
 * /ereg/studies/:protocolId/{folders,upload,signatures,delegation} routes.
 * Evolved from the old StudyDeepLink page, which hydrated context the same
 * way but then strip-navigated to context-only /ereg/folders URLs — losing
 * the study on refresh, back, and shared links.
 *
 * SiteScope does the same for the site-documents zone (/ereg/site/*).
 * LegacySectionRoute keeps the old context-driven URLs alive as redirects:
 * current study → last-opened study for the selected site (sessionStorage)
 * → dashboard. Signatures is user-global (N-5), so with no study it renders
 * the queue instead of bouncing.
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEreg } from '../contexts';
import * as eregApi from '../lib/api';
import { primaryBtnCls } from '../../ui';
import { sanitizeReturnTo } from '../../ui/returnTo';
import { SignatureQueue } from './SignatureQueue';

// ---- last-opened study persistence (per site, per tab) ----

const LAST_STUDY_PREFIX = 'ereg.lastStudy.';

function rememberLastStudy(siteId: string | null | undefined, protocolId: string) {
  if (!siteId) return;
  try {
    sessionStorage.setItem(`${LAST_STUDY_PREFIX}${siteId}`, protocolId);
  } catch {
    // storage blocked/full — the fallback is best-effort
  }
}

function recallLastStudy(siteId: string | null | undefined): string | null {
  if (!siteId) return null;
  try {
    return sessionStorage.getItem(`${LAST_STUDY_PREFIX}${siteId}`);
  } catch {
    return null;
  }
}

// Drop every site's pointer at a study that failed to open, so legacy-URL
// rehydration can't keep bouncing into the error page.
function forgetLastStudy(protocolId: string) {
  try {
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(LAST_STUDY_PREFIX) && sessionStorage.getItem(key) === protocolId) {
        stale.push(key);
      }
    }
    stale.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

// ---- /ereg/studies/:protocolId/* ----

export function StudyScope() {
  const { protocolId } = useParams<{ protocolId: string }>();
  const navigate = useNavigate();
  const { currentSite } = useAuth();
  const { currentProtocol, setCurrentProtocol, refreshFolders } = useEreg();
  const [error, setError] = useState<string | null>(null);

  const ready = !!protocolId && currentProtocol?.id === protocolId;

  // The site the study was last confirmed under. Lets us tell "the user
  // switched site away from this study" (leave the page) apart from "a deep
  // link opened another site's study" (fine — route guards resolve access
  // from the study's own site, and the global selector is left alone).
  const anchorSiteRef = useRef<string | null>(null);
  const leavingRef = useRef(false);

  useEffect(() => {
    leavingRef.current = false;
  }, [protocolId]);

  // Hydrate context from the URL whenever they disagree (refresh, new tab,
  // shared link, in-app study switch).
  useEffect(() => {
    if (!protocolId || ready || leavingRef.current) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const protocol = await eregApi.getProtocol(protocolId);
        if (cancelled || leavingRef.current) return;
        setCurrentProtocol(protocol);
        // Explicit prefetch so foldersLoading is already true when the nested
        // page mounts — Folders' ?folder_id= effect must never run against
        // the previous study's folder list.
        refreshFolders(protocol.id);
      } catch (err: any) {
        if (cancelled) return;
        forgetLastStudy(protocolId);
        setError(
          err?.response?.status === 404 || err?.response?.status === 403
            ? 'This study is not available for your account.'
            : 'Failed to open the study.'
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // setCurrentProtocol/refreshFolders are context fns recreated per render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolId, ready]);

  // Remember the anchored study (keyed by its own site) for legacy-URL
  // rehydration after the context is lost to a refresh or a new tab.
  useEffect(() => {
    if (!ready || !currentProtocol) return;
    rememberLastStudy(currentProtocol.site_id, currentProtocol.id);
    if (anchorSiteRef.current === null) {
      anchorSiteRef.current = currentSite?.id ?? null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, currentProtocol?.id]);

  // N-3: an explicit site switch away from this study's site leaves the page
  // instead of silently keeping the other site's study on screen.
  useEffect(() => {
    if (!ready || !currentProtocol || !currentSite) return;
    if (anchorSiteRef.current === null || anchorSiteRef.current === currentSite.id) {
      anchorSiteRef.current = currentSite.id;
      return;
    }
    anchorSiteRef.current = currentSite.id;
    if (currentProtocol.site_id && currentProtocol.site_id !== currentSite.id) {
      leavingRef.current = true;
      navigate('/ereg/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite?.id, ready]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg font-medium text-gray-900 dark:text-gray-100">{error}</p>
        <button onClick={() => navigate('/ereg/dashboard')} className={`mt-4 ${primaryBtnCls}`}>
          Go to eReg dashboard
        </button>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        Opening study…
      </div>
    );
  }

  return <Outlet />;
}

/**
 * /ereg/studies/:id index → folders, preserving the query string (?folder_id=
 * search deep links) and lifting the return_to convention into router state —
 * exactly the hand-off the old StudyDeepLink "home" mode performed, minus the
 * URL strip.
 */
export function StudyIndexRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnTo = sanitizeReturnTo(params.get('return_to'));
  const returnLabel = params.get('return_label') || undefined;
  params.delete('return_to');
  params.delete('return_label');
  const qs = params.toString();
  return (
    <Navigate
      to={`folders${qs ? `?${qs}` : ''}`}
      replace
      state={returnTo ? { returnTo, returnLabel } : undefined}
    />
  );
}

// ---- /ereg/site/* — the site-documents zone gets a canonical URL too ----

export function SiteScope() {
  const { siteMode, setSiteMode } = useEreg();

  // setSiteMode(true) also clears any selected study + folder (S-7 setters).
  useEffect(() => {
    if (!siteMode) setSiteMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteMode]);

  if (!siteMode) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        Opening site documents…
      </div>
    );
  }

  return <Outlet />;
}

// ---- legacy context-driven URLs ----

type LegacySection = 'folders' | 'upload' | 'signatures' | 'delegation';

export function LegacySectionRoute({ page }: { page: LegacySection }) {
  const location = useLocation();
  const { currentProtocol, siteMode } = useEreg();
  const { currentSite } = useAuth();

  if (currentProtocol) {
    return (
      <Navigate
        to={`/ereg/studies/${currentProtocol.id}/${page}${location.search}`}
        replace
        state={location.state}
      />
    );
  }
  if (siteMode && (page === 'folders' || page === 'upload')) {
    return <Navigate to={`/ereg/site/${page}${location.search}`} replace state={location.state} />;
  }
  if (page === 'signatures') {
    // The queue is user-global — render it rather than bouncing to the
    // dashboard when no study is selected.
    return <SignatureQueue />;
  }
  const lastStudyId = recallLastStudy(currentSite?.id);
  if (lastStudyId) {
    return (
      <Navigate
        to={`/ereg/studies/${lastStudyId}/${page}${location.search}`}
        replace
        state={location.state}
      />
    );
  }
  return <Navigate to="/ereg/dashboard" replace />;
}
